/**
 * ChannelBoard — Notion/Kanban drag-and-drop channel visibility configurator.
 *
 * ┌──────────────────────────────────────────────────────────────────┐
 * │  AVAILABLE CHANNELS (pool)                                        │
 * │  [≡ Compressor 1]  [≡ LP1]  [≡ Temperature]  [≡ Humidity]  …   │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * ┌────────────┐ ┌────────────┐ ┌────────────┐ ┌────────────┐
 * │  STATUS    │ │  REPORT    │ │ DASHBOARD  │ │   KPI      │
 * │  Live rdgs │ │  Trails    │ │  (max 3)   │ │  Charts    │
 * │            │ │            │ │            │ │            │
 * │ ≡ 1 Temp  │ │ ≡ 1 Water  │ │ ≡ 1 Temp  │ │ ≡ 1 Hum.  │
 * │ ≡ 2 Hum.  │ │ ≡ 2 Run T  │ │ ≡ 2 Hum.  │ │            │
 * │            │ │ ≡ 3 Idle…  │ │            │ │ Drop here  │
 * └────────────┘ └────────────┘ └────────────┘ └────────────┘
 *
 * – Drag chips from pool → column
 * – Drag items between columns
 * – Reorder within a column by dragging
 * – Click × to return a chip to the pool
 * – Report column: click "+ label" to set a column-header override
 *
 * Props
 *   available         string[]   all channel internal_names
 *   statusChannels    string[]
 *   reportChannels    string[]
 *   dashboardChannels string[]   capped at 3
 *   kpiChannels       string[]
 *   reportLabels      object     { [internal_name]: displayLabel }
 *   onChange          (field, value) => void
 *     fields: status_channels | report_channels | dashboard_channels
 *             | kpi_channels | report_channel_labels
 */
import React, { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MdDragIndicator, MdClose } from 'react-icons/md';

// ── Zone metadata ─────────────────────────────────────────────────────────────

const ZONES = ['status', 'report', 'dashboard', 'kpi'];

const ZONE_META = {
  status:    { title: 'Status',    sub: 'Live channel readings',       accent: '#2563eb', bg: '#eff6ff', cap: null },
  report:    { title: 'Report',    sub: 'Columns in trails & reports', accent: '#7c3aed', bg: '#f5f3ff', cap: null, hasLabels: true },
  dashboard: { title: 'Dashboard', sub: 'Metric pills — max 3',        accent: '#059669', bg: '#ecfdf5', cap: 3 },
  kpi:       { title: 'KPI',       sub: 'Hourly trend charts',         accent: '#d97706', bg: '#fffbeb', cap: null },
};

// ── Pool chip (draggable, horizontal wrap) ────────────────────────────────────

const PoolChip = ({ id }) => {
  const {
    attributes, listeners, setNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id, data: { type: 'pool' } });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        padding: '5px 10px 5px 6px',
        cursor: 'grab',
        userSelect: 'none',
        fontSize: 12.5,
        fontWeight: 500,
        color: '#374151',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.1s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.06)'; }}
      {...attributes}
      {...listeners}
    >
      <MdDragIndicator size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
      {id}
    </div>
  );
};

// ── Column item (sortable, with drag handle) ──────────────────────────────────

const ColumnItem = ({ id, zone, index, labels, onRemove, onLabelSave }) => {
  const {
    attributes, listeners, setNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id, data: { type: 'zone', zone } });

  const hasLabels = ZONE_META[zone]?.hasLabels;
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelVal, setLabelVal] = useState(labels[id] || '');

  const confirmLabel = () => {
    onLabelSave(id, labelVal.trim());
    setEditingLabel(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: '#fff',
        border: '1px solid #f0f2f5',
        borderRadius: 7,
        padding: '7px 8px',
        marginBottom: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      {/* Drag handle */}
      <span
        {...attributes}
        {...listeners}
        style={{ cursor: 'grab', color: '#c8d0da', display: 'flex', flexShrink: 0 }}
      >
        <MdDragIndicator size={15} />
      </span>

      {/* Index */}
      <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 16, textAlign: 'right', flexShrink: 0 }}>
        {index + 1}
      </span>

      {/* Channel name */}
      <span style={{
        flex: 1,
        fontSize: 13,
        fontWeight: 500,
        color: '#1e293b',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {id}
      </span>

      {/* Report label editor */}
      {hasLabels && (
        editingLabel ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
            <input
              autoFocus
              value={labelVal}
              onChange={(e) => setLabelVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmLabel();
                if (e.key === 'Escape') setEditingLabel(false);
              }}
              placeholder="Column label…"
              style={{
                fontSize: 11,
                border: '1.5px solid #7c3aed',
                borderRadius: 4,
                padding: '2px 6px',
                width: 96,
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <button
              onClick={confirmLabel}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#22c55e', fontSize: 14, padding: 0, lineHeight: 1 }}
            >✓</button>
            <button
              onClick={() => setEditingLabel(false)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 0, lineHeight: 1 }}
            >✕</button>
          </div>
        ) : (
          <button
            onClick={() => { setLabelVal(labels[id] || ''); setEditingLabel(true); }}
            title={labels[id] ? `Header label: "${labels[id]}" — click to edit` : 'Click to add a report column header label'}
            style={{
              flexShrink: 0,
              fontSize: 11,
              padding: '2px 7px',
              borderRadius: 4,
              cursor: 'pointer',
              border: `1px solid ${labels[id] ? '#c4b5fd' : '#e2e8f0'}`,
              background: labels[id] ? '#f5f3ff' : 'transparent',
              color: labels[id] ? '#7c3aed' : '#94a3b8',
              fontFamily: 'inherit',
              maxWidth: 88,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {labels[id] || '+ label'}
          </button>
        )
      )}

      {/* Remove button */}
      <button
        onClick={() => onRemove(id, zone)}
        title="Return to pool"
        style={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          color: '#c8d0da',
          padding: 2,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 4,
          flexShrink: 0,
          lineHeight: 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#c8d0da'; }}
      >
        <MdClose size={13} />
      </button>
    </div>
  );
};

// ── Zone column ───────────────────────────────────────────────────────────────

const ZoneColumn = ({ zone, items, labels, onRemove, onLabelSave }) => {
  const meta = ZONE_META[zone];
  const { setNodeRef, isOver } = useDroppable({ id: zone });
  const atCap = meta.cap != null && items.length >= meta.cap;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      background: '#fff',
      borderRadius: 10,
      border: `1.5px solid ${(isOver && !atCap) ? meta.accent : '#e8ecf0'}`,
      boxShadow: (isOver && !atCap)
        ? `0 0 0 3px ${meta.accent}1a, 0 1px 4px rgba(0,0,0,0.06)`
        : '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'border-color 0.12s, box-shadow 0.12s',
      minHeight: 220,
      overflow: 'hidden',
    }}>
      {/* Column header */}
      <div style={{
        padding: '12px 14px 10px',
        background: meta.bg,
        borderBottom: '1px solid #f0f2f5',
        borderRadius: '8px 8px 0 0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: meta.accent, letterSpacing: '0.01em' }}>
            {meta.title}
          </span>
          <span style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 20,
            background: atCap ? '#fef3c7' : `${meta.accent}1a`,
            color: atCap ? '#92400e' : meta.accent,
          }}>
            {items.length}{meta.cap != null ? `/${meta.cap}` : ''}
          </span>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.3 }}>{meta.sub}</div>
        {atCap && (
          <div style={{ fontSize: 10, color: '#d97706', marginTop: 4, fontWeight: 600 }}>
            ⚠ Max reached
          </div>
        )}
      </div>

      {/* Drop area */}
      <div ref={setNodeRef} style={{ flex: 1, padding: '8px 8px 4px', minHeight: 72 }}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {items.map((ch, idx) => (
            <ColumnItem
              key={ch}
              id={ch}
              zone={zone}
              index={idx}
              labels={labels}
              onRemove={onRemove}
              onLabelSave={onLabelSave}
            />
          ))}
        </SortableContext>

        {items.length === 0 && (
          <div style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1.5px dashed ${isOver ? meta.accent : '#dde3ea'}`,
            borderRadius: 7,
            fontSize: 12,
            color: isOver ? meta.accent : '#b0b8c4',
            fontWeight: isOver ? 600 : 400,
            transition: 'all 0.12s',
            userSelect: 'none',
          }}>
            {isOver ? '↓ Drop here' : 'Drag channels here'}
          </div>
        )}
      </div>
    </div>
  );
};

// ── ChannelBoard ──────────────────────────────────────────────────────────────

const ChannelBoard = ({
  available = [],
  statusChannels = [],
  reportChannels = [],
  dashboardChannels = [],
  kpiChannels = [],
  reportLabels = {},
  onChange,
}) => {
  const buildItems = () => {
    const assigned = new Set([
      ...statusChannels,
      ...reportChannels,
      ...dashboardChannels,
      ...kpiChannels,
    ]);
    return {
      pool:      available.filter((ch) => !assigned.has(ch)),
      status:    statusChannels,
      report:    reportChannels,
      dashboard: dashboardChannels.slice(0, 3),
      kpi:       kpiChannels,
    };
  };

  const [items, setItems] = useState(buildItems);
  const [labels, setLabels] = useState(reportLabels);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const allZones = ['pool', ...ZONES];

  const findContainer = useCallback((id) => {
    if (allZones.includes(id)) return id;
    for (const key of allZones) {
      if (items[key]?.includes(id)) return key;
    }
    return null;
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const fireChange = useCallback((newItems) => {
    onChange('status_channels',    newItems.status);
    onChange('report_channels',    newItems.report);
    onChange('dashboard_channels', newItems.dashboard.slice(0, 3));
    onChange('kpi_channels',       newItems.kpi);
  }, [onChange]);

  const onDragStart = ({ active }) => setActiveId(active.id);

  const onDragOver = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const activeContainer = findContainer(active.id);
    const overContainer   = findContainer(over.id);

    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    // Enforce dashboard cap
    const dashCap = ZONE_META.dashboard.cap;
    if (overContainer === 'dashboard' && dashCap != null) {
      if (items.dashboard.length >= dashCap && !items.dashboard.includes(active.id)) return;
    }

    setItems((prev) => {
      const sourceItems = prev[activeContainer].filter((id) => id !== active.id);
      const destItems   = [...prev[overContainer]];
      const overIndex   = destItems.indexOf(over.id);
      const insertAt    = overIndex >= 0 ? overIndex : destItems.length;
      destItems.splice(insertAt, 0, active.id);
      return { ...prev, [activeContainer]: sourceItems, [overContainer]: destItems };
    });
  };

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;

    const activeContainer = findContainer(active.id);
    const overContainer   = findContainer(over.id);
    if (!activeContainer || !overContainer) return;

    if (activeContainer === overContainer) {
      const idx1 = items[activeContainer].indexOf(active.id);
      const idx2 = items[activeContainer].indexOf(over.id);
      if (idx1 !== -1 && idx2 !== -1 && idx1 !== idx2) {
        setItems((prev) => {
          const newItems = {
            ...prev,
            [activeContainer]: arrayMove(prev[activeContainer], idx1, idx2),
          };
          if (activeContainer !== 'pool') fireChange(newItems);
          return newItems;
        });
        return;
      }
    }
    // Cross-container move already applied in onDragOver; just fire onChange
    if (activeContainer !== 'pool' || overContainer !== 'pool') {
      fireChange(items);
    }
  };

  const onRemove = useCallback((channelId, zone) => {
    setItems((prev) => {
      const newItems = {
        ...prev,
        [zone]: prev[zone].filter((ch) => ch !== channelId),
        pool:   [...prev.pool, channelId],
      };
      fireChange(newItems);
      return newItems;
    });
  }, [fireChange]);

  const onLabelSave = useCallback((channelId, newLabel) => {
    setLabels((prev) => {
      const next = { ...prev };
      if (newLabel) next[channelId] = newLabel;
      else delete next[channelId];
      onChange('report_channel_labels', next);
      return next;
    });
  }, [onChange]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      {/* ── Pool ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: 8,
        }}>
          Available Channels — drag into sections below
        </div>
        <div style={{
          background: '#f8fafc',
          border: '1.5px dashed #cbd5e1',
          borderRadius: 10,
          padding: '12px 14px',
          minHeight: 52,
        }}>
          <SortableContext items={items.pool} strategy={rectSortingStrategy}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {items.pool.map((ch) => <PoolChip key={ch} id={ch} />)}
              {items.pool.length === 0 && (
                <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', padding: '2px 0' }}>
                  ✓ All channels have been assigned to sections
                </span>
              )}
            </div>
          </SortableContext>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
          Tip: Leave a section empty to show all channels there by default.
          In the <strong>Report</strong> column, click "+ label" to rename a column header.
        </div>
      </div>

      {/* ── 4-column zone grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {ZONES.map((zone) => (
          <ZoneColumn
            key={zone}
            zone={zone}
            items={items[zone]}
            labels={labels}
            onRemove={onRemove}
            onLabelSave={onLabelSave}
          />
        ))}
      </div>

      {/* ── Drag overlay ── */}
      <DragOverlay>
        {activeId ? (
          <div style={{
            background: '#fff',
            border: '1.5px solid #3b82f6',
            borderRadius: 7,
            padding: '7px 14px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            fontSize: 13,
            fontWeight: 600,
            color: '#1e293b',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            cursor: 'grabbing',
            userSelect: 'none',
          }}>
            <MdDragIndicator size={16} style={{ color: '#3b82f6' }} />
            {activeId}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ChannelBoard;
